/** Deterministic hash / value-noise helpers for procedural pixel art. */

export function hash2(x: number, y: number, s = 0): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(s | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Value noise in [0,1). */
export function vnoise(x: number, y: number, s = 0): number {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = smooth(x - xi), yf = smooth(y - yi);
  const a = hash2(xi, yi, s), b = hash2(xi + 1, yi, s);
  const c = hash2(xi, yi + 1, s), d = hash2(xi + 1, yi + 1, s);
  return a + (b - a) * xf + (c - a) * yf + (a - b - c + d) * xf * yf;
}

/** Fractal value noise in [0,1). */
export function fbm(x: number, y: number, oct = 3, s = 0): number {
  let sum = 0, amp = 0.5, norm = 0, f = 1;
  for (let i = 0; i < oct; i++) {
    sum += vnoise(x * f, y * f, s + i * 17) * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2;
  }
  return sum / norm;
}

/** Tileable-looking Worley F1 distance (cells of size `cell`), in cell units. */
export function worley(x: number, y: number, cell: number, s = 0): number {
  const cx = Math.floor(x / cell), cy = Math.floor(y / cell);
  let best = 9;
  for (let j = -1; j <= 1; j++) {
    for (let i = -1; i <= 1; i++) {
      const px = (cx + i + hash2(cx + i, cy + j, s)) * cell;
      const py = (cy + j + hash2(cx + i, cy + j, s + 7)) * cell;
      const d = Math.hypot(x - px, y - py) / cell;
      if (d < best) best = d;
    }
  }
  return best;
}

/** 4x4 Bayer threshold in [0,1). */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
export function bayer(x: number, y: number): number {
  return (BAYER[((y & 3) << 2) | (x & 3)]! + 0.5) / 16;
}
